import React from "react";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { translateBackendMessage } from "../../../utilities/translateBackendMessage";

const STATUS_CONFIG = {
  E: { color: "#ef4444", bgcolor: "#fee2e2", icon: <ErrorIcon sx={{ fontSize: 16, color: "#ef4444" }} /> },
  S: { color: "#16a34a", bgcolor: "#dcfce7", icon: <CheckCircleIcon sx={{ fontSize: 16, color: "#16a34a" }} /> },
  W: { color: "#d97706", bgcolor: "#fef3c7", icon: <WarningAmberIcon sx={{ fontSize: 16, color: "#d97706" }} /> },
  I: { color: "#2563eb", bgcolor: "#eff6ff", icon: <InfoOutlinedIcon sx={{ fontSize: 16, color: "#2563eb" }} /> },
};

const getCfg = (status) =>
  STATUS_CONFIG[String(status ?? "").toUpperCase()] ?? STATUS_CONFIG["I"];

export default function ProcessMessagesTable({ messages = [] }) {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language?.startsWith("en");
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const chipBg = (color, pastelBg) => (isDark ? alpha(color, 0.18) : pastelBg);

  return (
    <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
      <TableHead>
        <TableRow
          sx={{
            "& th": {
              bgcolor: "background.subtle",
              borderBottom: "2px solid",
              borderColor: "divider",
              fontWeight: 700,
              fontSize: "0.7rem",
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              py: 1.5,
              px: 2,
            },
          }}
        >
          <TableCell width={70}>{t("log.step")}</TableCell>
          <TableCell width={100}>{t("log.sequentialStep")}</TableCell>
          <TableCell width={80} align="center">{t("log.Status")}</TableCell>
          <TableCell width={120}>{t("log.number")}</TableCell>
          <TableCell>{t("log.message")}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {messages.map((msg, idx) => {
          const cfg = getCfg(msg.status_message);
          const skip = ["", "0", "000", "000000"];
          const idParts = [msg.id_msg, msg.number_msg]
            .map((v) => String(v ?? "").trim())
            .filter((v) => v && !skip.includes(v));

          return (
            <TableRow
              key={idx}
              sx={{
                "& td": {
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  fontSize: "0.8rem",
                  color: "text.primary",
                  py: 1.25,
                  px: 2,
                },
                "& td:first-of-type": {
                  borderLeft: `3px solid ${cfg.color}`,
                  pl: "13px",
                },
                "&:last-child td": { borderBottom: 0 },
              }}
            >
              <TableCell sx={{ fontWeight: 600 }}>{msg.step ?? "—"}</TableCell>
              <TableCell>{msg.sequential_step ?? "—"}</TableCell>
              <TableCell align="center">
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: chipBg(cfg.color, cfg.bgcolor),
                    p: 0.6,
                    borderRadius: "6px",
                  }}
                >
                  {cfg.icon}
                </Box>
              </TableCell>
              <TableCell sx={{ fontFamily: "monospace", color: "text.secondary", fontSize: "0.75rem" }}>
                {idParts.join(" / ") || "—"}
              </TableCell>
              <TableCell sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                {translateBackendMessage(msg.message_tx, isEnglish) || "—"}
              </TableCell>
            </TableRow>
          );
        })}
        {messages.length === 0 && (
          <TableRow>
            <TableCell colSpan={5}>
              <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", textAlign: "center", py: 2 }}>
                {t("table.empty")}
              </Typography>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
